import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  ChartBarIcon,
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

const PayrollHistory = () => {
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState('2025');
  const [selectedView, setSelectedView] = useState('table'); // 'table' or 'chart'
  const [showFilters, setShowFilters] = useState(false);

  // Mock payroll history data
  const payrollHistory = {
    '2025': [
      { month: 'July 2025', basicSalary: 50000, allowances: 35000, overtime: 3500, deductions: 12500, netSalary: 76000, status: 'Processed' },
      { month: 'June 2025', basicSalary: 50000, allowances: 33000, overtime: 2800, deductions: 12200, netSalary: 73600, status: 'Processed' },
      { month: 'May 2025', basicSalary: 50000, allowances: 32500, overtime: 4200, deductions: 12300, netSalary: 74400, status: 'Processed' },
      { month: 'April 2025', basicSalary: 50000, allowances: 32000, overtime: 3800, deductions: 12100, netSalary: 73700, status: 'Processed' },
      { month: 'March 2025', basicSalary: 48000, allowances: 31000, overtime: 2500, deductions: 11800, netSalary: 69700, status: 'Processed' },
      { month: 'February 2025', basicSalary: 48000, allowances: 30500, overtime: 3200, deductions: 11700, netSalary: 70000, status: 'Processed' },
      { month: 'January 2025', basicSalary: 48000, allowances: 30000, overtime: 2800, deductions: 11600, netSalary: 69200, status: 'Processed' }
    ],
    '2024': [
      { month: 'December 2024', basicSalary: 48000, allowances: 29500, overtime: 4000, deductions: 11500, netSalary: 70000, status: 'Processed' },
      { month: 'November 2024', basicSalary: 45000, allowances: 28000, overtime: 3500, deductions: 11200, netSalary: 65300, status: 'Processed' },
      { month: 'October 2024', basicSalary: 45000, allowances: 27500, overtime: 2800, deductions: 11000, netSalary: 64300, status: 'Processed' },
      { month: 'September 2024', basicSalary: 45000, allowances: 27000, overtime: 3200, deductions: 10900, netSalary: 64300, status: 'Processed' },
      { month: 'August 2024', basicSalary: 45000, allowances: 26500, overtime: 2500, deductions: 10800, netSalary: 63200, status: 'Processed' },
      { month: 'July 2024', basicSalary: 42000, allowances: 25000, overtime: 3000, deductions: 10500, netSalary: 59500, status: 'Processed' }
    ]
  };

  const yearlyStats = {
    '2025': {
      totalGross: 573300,
      totalNet: 506600,
      totalDeductions: 66700,
      avgMonthlySalary: 72400,
      highestSalary: 76000,
      lowestSalary: 69200,
      totalOvertimeHours: 168,
      overtimeEarnings: 22800
    },
    '2024': {
      totalGross: 432800,
      totalNet: 387100,
      totalDeductions: 45700,
      avgMonthlySalary: 64516,
      highestSalary: 70000,
      lowestSalary: 59500,
      totalOvertimeHours: 124,
      overtimeEarnings: 19000
    }
  };

  const availableYears = ['2025', '2024', '2023', '2022'];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Processed': return 'text-green-600 bg-green-100';
      case 'Pending': return 'text-yellow-600 bg-yellow-100';
      case 'Failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const calculateGrowth = (current, previous) => {
    if (!previous) return 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const handleDownload = (month) => {
    alert(`Downloading payslip for ${month}`);
  };

  const currentHistory = payrollHistory[selectedYear] || [];
  const currentStats = yearlyStats[selectedYear] || {};
  const previousYearStats = yearlyStats[String(parseInt(selectedYear) - 1)] || {};

  const salaryGrowth = calculateGrowth(currentStats.avgMonthlySalary, previousYearStats.avgMonthlySalary);

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
              <button
                onClick={() => navigate('/payroll')}
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apollo-primary"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Payroll
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Payroll History</h1>
                <p className="mt-1 sm:mt-2 text-base sm:text-lg text-gray-600">View your salary history and trends</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
              </button>
              
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-apollo-primary focus:border-apollo-primary rounded-md"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>

              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  onClick={() => setSelectedView('table')}
                  className={`px-4 py-2 text-sm font-medium ${
                    selectedView === 'table'
                      ? 'bg-apollo-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Table
                </button>
                <button
                  onClick={() => setSelectedView('chart')}
                  className={`px-4 py-2 text-sm font-medium ${
                    selectedView === 'chart'
                      ? 'bg-apollo-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Chart
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mb-6 sm:mb-8 bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Filters</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
                <select className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-apollo-primary focus:border-apollo-primary text-sm">
                  <option>All Months</option>
                  <option>Last 6 Months</option>
                  <option>Last 3 Months</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Salary Range</label>
                <select className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-apollo-primary focus:border-apollo-primary text-sm">
                  <option>All Ranges</option>
                  <option>Above ₹70,000</option>
                  <option>₹50,000 - ₹70,000</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-apollo-primary focus:border-apollo-primary text-sm">
                  <option>All Status</option>
                  <option>Processed</option>
                  <option>Pending</option>
                </select>
              </div>
              <div className="flex items-end">
                <button className="w-full bg-apollo-primary text-white px-4 py-2 rounded-md hover:bg-apollo-secondary">
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Yearly Statistics */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-2 sm:space-y-0">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Yearly Statistics - {selectedYear}</h3>
              <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-apollo-primary" />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <div className="text-center p-4 bg-gradient-to-br from-apollo-primary to-apollo-accent rounded-lg text-white">
                <div className="text-xl sm:text-2xl font-bold">{formatCurrency(currentStats.totalNet)}</div>
                <div className="text-apollo-primary-100 text-sm">Total Net Earnings</div>
                <div className="flex items-center justify-center mt-2">
                  {salaryGrowth > 0 ? (
                    <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
                  )}
                  <span className="text-xs sm:text-sm">{Math.abs(salaryGrowth)}% vs last year</span>
                </div>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(currentStats.avgMonthlySalary)}</div>
                <div className="text-gray-600 text-sm">Average Monthly Salary</div>
                <div className="text-xs sm:text-sm text-gray-500 mt-1">Based on {currentHistory.length} months</div>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-xl sm:text-2xl font-bold text-green-600">{formatCurrency(currentStats.highestSalary)}</div>
                <div className="text-gray-600 text-sm">Highest Salary</div>
                <div className="text-xs sm:text-sm text-gray-500 mt-1">Peak earning month</div>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-xl sm:text-2xl font-bold text-blue-600">{formatCurrency(currentStats.overtimeEarnings)}</div>
                <div className="text-gray-600 text-sm">Overtime Earnings</div>
                <div className="text-xs sm:text-sm text-gray-500 mt-1">{currentStats.totalOvertimeHours} hours total</div>
              </div>
            </div>
          </div>
        </div>

        {/* Payroll History Table/Chart */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Payroll Records</h3>
              <button
                onClick={() => alert('Downloading yearly payroll summary')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Download Summary
              </button>
            </div>
          </div>

          {selectedView === 'table' ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Basic Salary</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Allowances</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overtime</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deductions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Salary</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentHistory.map((record, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <CalendarDaysIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">{record.month}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(record.basicSalary)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(record.allowances)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">{formatCurrency(record.overtime)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">-{formatCurrency(record.deductions)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <BanknotesIcon className="h-4 w-4 text-apollo-primary mr-2" />
                          <span className="text-sm font-bold text-apollo-primary">{formatCurrency(record.netSalary)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(record.status)}`}>
                          {record.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleDownload(record.month)}
                          className="text-apollo-primary hover:text-apollo-secondary"
                        >
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6">
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <ChartBarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Chart View</h3>
                <p className="text-gray-600">Interactive salary chart visualization would be implemented here using Chart.js or similar library.</p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg">
                    <div className="text-sm text-gray-600">Trend Analysis</div>
                    <div className="text-lg font-bold text-apollo-primary">↗ Upward</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <div className="text-sm text-gray-600">Avg Growth</div>
                    <div className="text-lg font-bold text-green-600">+8.5%</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <div className="text-sm text-gray-600">Variability</div>
                    <div className="text-lg font-bold text-blue-600">Low</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white">
            <h4 className="text-lg font-semibold mb-2">Total Gross Earnings</h4>
            <div className="text-3xl font-bold">{formatCurrency(currentStats.totalGross)}</div>
            <div className="text-green-100 text-sm mt-2">Including all allowances and overtime</div>
          </div>
          
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-6 text-white">
            <h4 className="text-lg font-semibold mb-2">Total Deductions</h4>
            <div className="text-3xl font-bold">{formatCurrency(currentStats.totalDeductions)}</div>
            <div className="text-red-100 text-sm mt-2">Tax, PF, and other deductions</div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
            <h4 className="text-lg font-semibold mb-2">Net Take Home</h4>
            <div className="text-3xl font-bold">{formatCurrency(currentStats.totalNet)}</div>
            <div className="text-blue-100 text-sm mt-2">Amount credited to your account</div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PayrollHistory;
